<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed, nextTick } from 'vue';
    import { DisplayStyle, TextInputType, ButtonDisplaySize } from '../../Enums/UiEnums';
    import { PasswordQualityOptions, passwordQualityIssues } from '../../lib/Validation/PasswordValidation'
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { CssLayoutUnits } from '../../lib/CssHelpers';

    import TextInput from '../../components/TextInput.vue';
    import MessageBadge from '../../components/Messages/MessageBadge.vue'
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';

    import * as ChoiceApi from '../../lib/ChoiceApi';

    const emit = defineEmits<{
        (e: "onUpdatePassword", currentPassword: string, password: string) : void
    }>();

    defineExpose({displayPasswordQualityIssues, displayFocus});

    const existingPasswordInput = ref();
    const UpdateButton = ref<InstanceType<typeof ActionButtonV2>>();

    const props = withDefaults(defineProps<{
        applyUpdate?: boolean
    }>(),
    {
    });

    const formData = reactive<{
        existingPassword?: string,
        newPassword?: string,
        newPasswordConfirmation?: string,
        newPasswordQualityIssues?: string[]
        minPasswordLen?: number,
        successMessage: string
    }>({
        successMessage: "Ready to update password"
    });

    const passwordQualitySettings = computed<PasswordQualityOptions>(() => { return {
        minimumLength: formData.minPasswordLen ?? 12,
        atLeastOneNumber: true,
        atLeastOneUpperCaseLetter: true, 
        atLeastOneLowerCaseLetter: true
    }});

    onMounted(async () => {
        formData.newPasswordQualityIssues = passwordQualityIssues("", passwordQualitySettings.value);
        displayFocus();
    });

    const passwordIssues = computed(() =>{
        const issues = [];

        if ((formData.existingPassword ?? '').length < 1)
            issues.push("Current password is required");

        if (formData.newPassword != formData.newPasswordConfirmation)
            issues.push("New password doesn't match the confirmation of the new password");

        if (formData.newPasswordQualityIssues)
            issues.push(...formData.newPasswordQualityIssues);
        return issues;
    });

    async function onUpdatePassword() {
        formData.newPasswordQualityIssues = passwordQualityIssues((formData.newPassword ?? '').toString(), passwordQualitySettings.value);
        if (passwordIssues.value.length > 0)
            return;
        if (props.applyUpdate) {
            const inputs = {
                oldPassword: formData.existingPassword ?? "",
                password: formData.newPassword ?? ""
            }
            const result = await ChoiceApi.user.UpdatePassword(inputs);
            formData.newPasswordQualityIssues = result?.passwordIssues;
            if (formData.newPasswordQualityIssues?.length == 0) {
                formData.successMessage = "Password updated successfully";
            }
        }
        else {
            emit("onUpdatePassword",formData.existingPassword ?? "",formData.newPassword ?? "");
        }
    }

    async function onKeyUpdate() {
        await UpdateButton.value?.actionButtonClick();
    }

    async function displayPasswordQualityIssues(passwordQualityIssues: string[]) {
        formData.newPasswordQualityIssues = passwordQualityIssues;
    }

    async function displayFocus() {
        await nextTick();
        existingPasswordInput.value?.focus();
    }

</script>

<template>
    <div class="mb-2">
        <div class="mt-1">
            Current Password
        </div>
        <div>
            <TextInput 
                ref="existingPasswordInput"
                placeholder="Current Password"
                autoCompleteType="current-password"
                v-model:text-value="formData.existingPassword"
                :inputType="TextInputType.password"
                @keydown.enter="onKeyUpdate" />
        </div>
    </div>

    <div class="mb-2">
        <div class="mt-1">
            New Password
        </div>
        <div>
            <TextInput
                v-model:text-value="formData.newPassword"
                placeholder="New Password"
                :inputType="TextInputType.password"
                autoCompleteType="new-password"
                @update:text-value="(newVal: any) => formData.newPasswordQualityIssues = passwordQualityIssues((newVal ?? '').toString(), passwordQualitySettings)"
                @keydown.enter="onKeyUpdate" />
        </div>
    </div>

    <div class="mb-2">
        <div class="mt-1">
            Confirm New Password
        </div>
        <div>
            <TextInput
                v-model:text-value="formData.newPasswordConfirmation"
                :inputType="TextInputType.password"
                autoCompleteType="new-password"
                placeholder="Confirm New Password"
                @keydown.enter="onKeyUpdate" />
        </div>
    </div>

    <div class="alert alert-danger" v-if="passwordIssues.length">
        <MessageBadge :displayStyle="DisplayStyle.Danger">
            Password Issues
        </MessageBadge>            
        <ul>
            <li v-for="issue in passwordIssues">
                {{issue}}
            </li>
        </ul>
    </div>
    <div class="alert alert-success" v-if="passwordIssues.length == 0">
        {{ formData.successMessage}}
    </div>
    <div>
        <ActionButtonV2
            ref="UpdateButton" 
            :action="ActionButtonStyle.Save" 
            text="Update Password" 
            :size="ButtonDisplaySize.Small"
            :width="{ amount: 150, units: CssLayoutUnits.px}"
            :performAction="async () => await onUpdatePassword()" />
    </div>
</template>

